import React, { useState, useEffect } from 'react';
import { misDatosApi } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Save, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const PROVINCIAS = [
  'BUENOS AIRES', 'CABA', 'CATAMARCA', 'CHACO', 'CHUBUT', 'CORDOBA', 'CORRIENTES',
  'ENTRE RIOS', 'FORMOSA', 'JUJUY', 'LA PAMPA', 'LA RIOJA', 'MENDOZA', 'MISIONES',
  'NEUQUEN', 'RIO NEGRO', 'SALTA', 'SAN JUAN', 'SAN LUIS', 'SANTA CRUZ', 'SANTA FE',
  'SANTIAGO DEL ESTERO', 'TIERRA DEL FUEGO', 'TUCUMAN'
];

const CONDICIONES_IVA = [
  { value: 'responsable_inscripto', label: 'Responsable Inscripto' },
  { value: 'monotributista', label: 'Monotributista' },
  { value: 'exento', label: 'Exento' },
  { value: 'consumidor_final', label: 'Consumidor Final' },
];

const TABS = [
  { id: 'personal', label: 'Personal' },
  { id: 'domicilio', label: 'Domicilio' },
  { id: 'fiscal', label: 'Fiscal' },
  { id: 'bancario', label: 'Bancario' },
];

// Definido a NIVEL DE MÓDULO (identidad estable): evita que el input se
// desmonte/remonte en cada tecla y que iOS cierre el teclado.
const Field = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">{label}</label>
    <Input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || ''}
      className="text-sm"
    />
  </div>
);

export default function PropietarioMisDatos() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');

  const [whatsappDistinto, setWhatsappDistinto] = useState(false);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (data && data.whatsapp && data.telefono && data.whatsapp !== data.telefono) {
      setWhatsappDistinto(true);
    }
  }, [data?.whatsapp, data?.telefono]);

  const fetchData = async () => {
    try {
      const res = await misDatosApi.get();
      setData(res.data);
    } catch (err) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = { ...data };
      if (!whatsappDistinto) toSave.whatsapp = toSave.telefono || '';
      await misDatosApi.update(toSave);
      toast.success('Datos actualizados');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-3 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-base md:text-lg font-semibold text-slate-900">Mis Datos</h1>
        <Button onClick={handleSave} disabled={saving} size="sm" className="h-7 text-xs bg-[#1B4D3E] hover:bg-[#163d32]" data-testid="save-mis-datos">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Guardar
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4">
        <div className="flex gap-0 overflow-x-auto scrollbar-none -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={cn(
                "px-3 md:px-4 py-2 text-xs md:text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                activeTab === tab.id
                  ? "border-[#1B4D3E] text-[#1B4D3E]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 md:p-5">
        {activeTab === 'personal' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre / Razón Social" value={data.nombre_razon_social} onChange={(v) => handleChange('nombre_razon_social', v)} />
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tipo</label>
              <Select value={data.tipo || ''} onValueChange={(v) => handleChange('tipo', v)}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="persona_humana">Persona Humana</SelectItem>
                  <SelectItem value="persona_juridica">Persona Jurídica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Email" value={data.email} onChange={(v) => handleChange('email', v)} type="email" />
            <Field label="Teléfono / WhatsApp" value={data.telefono} onChange={(v) => handleChange('telefono', v)} placeholder="+54..." />
            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={whatsappDistinto}
                  onChange={(e) => {
                    setWhatsappDistinto(e.target.checked);
                    if (!e.target.checked) handleChange('whatsapp', data.telefono || '');
                  }}
                  className="rounded border-slate-300 text-[#1B4D3E] focus:ring-[#1B4D3E] w-3.5 h-3.5"
                />
                <span className="text-xs text-slate-600">Mi WhatsApp es distinto al teléfono</span>
              </label>
            </div>
            {whatsappDistinto && (
              <Field label="WhatsApp" value={data.whatsapp} onChange={(v) => handleChange('whatsapp', v)} placeholder="+54..." />
            )}
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Preferencia de Contacto</label>
              <Select value={data.preferencia_contacto || ''} onValueChange={(v) => handleChange('preferencia_contacto', v)}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="telefono">Teléfono</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {activeTab === 'domicilio' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Dirección" value={data.direccion} onChange={(v) => handleChange('direccion', v)} />
            <Field label="Localidad" value={data.localidad} onChange={(v) => handleChange('localidad', v)} />
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Provincia</label>
              <Select value={data.provincia || ''} onValueChange={(v) => handleChange('provincia', v)}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {PROVINCIAS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Código Postal" value={data.codigo_postal} onChange={(v) => handleChange('codigo_postal', v)} />
          </div>
        )}

        {activeTab === 'fiscal' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="CUIT / CUIL" value={data.cuit_cuil} onChange={(v) => handleChange('cuit_cuil', v)} placeholder="XX-XXXXXXXX-X" />
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Condición IVA</label>
              <Select value={data.condicion_iva || ''} onValueChange={(v) => handleChange('condicion_iva', v)}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {CONDICIONES_IVA.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Domicilio Fiscal" value={data.domicilio_fiscal} onChange={(v) => handleChange('domicilio_fiscal', v)} />
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Tipo de Factura</label>
              <Select value={data.tipo_factura || ''} onValueChange={(v) => handleChange('tipo_factura', v)}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">Factura A</SelectItem>
                  <SelectItem value="B">Factura B</SelectItem>
                  <SelectItem value="C">Factura C</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {activeTab === 'bancario' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="CBU" value={data.cbu} onChange={(v) => handleChange('cbu', v)} />
            <Field label="Alias CBU" value={data.alias_cbu} onChange={(v) => handleChange('alias_cbu', v)} />
            <Field label="Banco" value={data.banco} onChange={(v) => handleChange('banco', v)} />
            <Field label="Titular de la Cuenta" value={data.titular_cuenta} onChange={(v) => handleChange('titular_cuenta', v)} />
          </div>
        )}
      </div>
    </div>
  );
}
